import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faPen } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getFragmentData } from '~/gql';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { SideLayout } from '~/layouts/side/SideLayout';
import { useSiteQuery } from '~/routes/sites.$siteId';
import { hasDisplayableAttributes } from '../AttributeView';
import {
  AttributeFieldsFragment,
  AttributesList,
} from '../sites/tabs/AttributesList';
import { AddressLink } from '../ui/Address';
import { BuildingPlaceholder } from '../ui/BuildingPlaceholder';
import { Gallery } from '../ui/Gallery';
import { Loading } from '../ui/Loading';
import { TabHeader } from '../ui/TabHeader';

// Constants for timing and delays
const TIMING = {
  ACTIVITY_LOAD_DELAY: 100,
  JOBS_LOAD_DELAY: 200, // 300ms total (100 + 200)
  SPACES_LOAD_DELAY: 200, // 500ms total (100 + 200 + 200)
  ASSETS_LOAD_DELAY: 200, // 700ms total (100 + 200 + 200 + 200)
} as const;

// Lazy load tab components
const ActivityTab = lazy(() =>
  import('../sites/tabs/ActivityTab').then((module) => ({
    default: module.ActivityTab,
  }))
);
const JobsList = lazy(() =>
  import('../sites/tabs/JobsTab').then((module) => ({
    default: module.JobsList,
  }))
);
const SpacesList = lazy(() =>
  import('../sites/tabs/SpacesTab').then((module) => ({
    default: module.SpacesList,
  }))
);
const AssetsList = lazy(() =>
  import('../sites/tabs/AssetsTab').then((module) => ({
    default: module.AssetsList,
  }))
);

type Props = {
  siteId: string;
  onClose: () => void;
  isPopup?: boolean;
};

export function SiteView({ siteId, onClose, isPopup }: Props) {
  const { t } = useTranslation();
  const [{ data, fetching }, reload] = useSiteQuery(siteId);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const { isMobile } = useBreakpoint();
  const [isSearchSticked, setIsSearchSticked] = useState(false);
  // Track which tabs have been loaded to enable sequential lazy loading
  const [loadedTabs, setLoadedTabs] = useState<Set<number>>(new Set([0])); // Attributes tab loads immediately
  // Track if background loading has been initialized to prevent duplicate triggers
  const backgroundLoadingInitialized = useRef(false);
  // Track if component is mounted to prevent async state updates after unmount
  const isMountedRef = useRef(true);
  // Centralized timer management for background loading
  const timersRef = useRef<{
    activity: NodeJS.Timeout | null;
    jobs: NodeJS.Timeout | null;
    spaces: NodeJS.Timeout | null;
    assets: NodeJS.Timeout | null;
  }>({
    activity: null,
    jobs: null,
    spaces: null,
    assets: null,
  });

  // Helper function to clear all background loading timers
  // Also resets the initialization flag so sequential loading can restart after refetch
  const clearAllTimers = useCallback(() => {
    (
      Object.keys(timersRef.current) as Array<keyof typeof timersRef.current>
    ).forEach((key) => {
      const timer = timersRef.current[key];
      if (timer) {
        clearTimeout(timer);
        timersRef.current[key] = null;
      }
    });
    // Reset initialization flag whenever timers are cleared
    // This ensures sequential loading can restart after refetch completes
    backgroundLoadingInitialized.current = false;
  }, []);

  const containerEdge = isMobile ? 0 : 161;

  useEffect(() => {
    const scrollContainer = document.querySelector('#site-view-container');
    if (!scrollContainer) return;

    const onScroll = () => {
      const searchEl = scrollContainer?.querySelector('#search-attributes');
      if (!searchEl) return;

      const { top } = searchEl.getBoundingClientRect();
      const visible = isMobile ? top === containerEdge : top <= containerEdge;
      setIsSearchSticked(visible);
    };

    scrollContainer.addEventListener('scroll', onScroll);

    return () => {
      scrollContainer.removeEventListener('scroll', onScroll);
    };
  }, [isMobile, containerEdge]);

  // Background loading: Start loading tabs automatically once site data is loaded
  useEffect(() => {
    // Clear any existing timers first to prevent race conditions
    clearAllTimers();

    const site = data?.site;

    // Only start background loading if site is loaded and component is mounted
    if (
      site &&
      !fetching &&
      !backgroundLoadingInitialized.current &&
      isMountedRef.current
    ) {
      // Mark as initialized to prevent duplicate triggers
      backgroundLoadingInitialized.current = true;

      // Sequential loading configuration: tab index -> delay -> timer key
      const loadingSequence: Array<{
        tabIndex: number;
        delay: number;
        timerKey: keyof typeof timersRef.current;
      }> = [
        {
          tabIndex: 1,
          delay: TIMING.ACTIVITY_LOAD_DELAY,
          timerKey: 'activity',
        },
        { tabIndex: 2, delay: TIMING.JOBS_LOAD_DELAY, timerKey: 'jobs' },
        { tabIndex: 3, delay: TIMING.SPACES_LOAD_DELAY, timerKey: 'spaces' },
        { tabIndex: 4, delay: TIMING.ASSETS_LOAD_DELAY, timerKey: 'assets' },
      ];

      // Linear sequential loading: each timer starts the next one
      // This preserves the benefits of chaining (one timer at a time, automatic cancellation)
      // while improving readability and maintainability
      const scheduleNext = (index: number) => {
        if (index >= loadingSequence.length || !isMountedRef.current) return;

        const { tabIndex, delay, timerKey } = loadingSequence[index];
        timersRef.current[timerKey] = setTimeout(() => {
          if (!isMountedRef.current) return;
          timersRef.current[timerKey] = null;

          setLoadedTabs((prev) => {
            if (prev.has(tabIndex)) return prev; // Already loaded
            return new Set([...prev, tabIndex]);
          });

          // Schedule next tab in sequence
          scheduleNext(index + 1);
        }, delay);
      };

      // Start the sequential loading chain
      scheduleNext(0);
    }

    // Cleanup function - always clear timers if effect re-runs
    return clearAllTimers;
  }, [data, fetching, clearAllTimers]);

  // Component mount/unmount tracking with timer cleanup
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      // Clear all timers on unmount
      clearAllTimers();
    };
  }, [clearAllTimers]); // clearAllTimers is stable (useCallback with empty deps)

  // Sequential lazy loading: Load tabs in order when user selects them
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    // Mark current tab as loaded and preload next tab in sequence
    // Using functional update to always work with latest state
    setLoadedTabs((prev) => {
      const updated = new Set(prev);
      let hasChanges = false;

      // Mark current tab as loaded when selected
      if (!prev.has(selectedTabIndex)) {
        updated.add(selectedTabIndex);
        hasChanges = true;
      }

      // Preload next tab in sequence after current tab is selected
      // This ensures tabs load in order: Attributes -> Activity -> Jobs -> Spaces -> Assets
      if (selectedTabIndex < 4) {
        const nextTabIndex = selectedTabIndex + 1;
        if (!prev.has(nextTabIndex)) {
          // Preload next tab after a short delay
          timer = setTimeout(() => {
            if (!isMountedRef.current) return;
            setLoadedTabs((current) => new Set([...current, nextTabIndex]));
          }, 200);
        }
      }

      // Return same reference if no change to prevent unnecessary re-renders
      return hasChanges ? updated : prev;
    });

    // Cleanup timeout if effect re-runs or component unmounts
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [selectedTabIndex]); // Only depend on selectedTabIndex to prevent chain reactions

  const site = data?.site;
  if (!site) {
    return <Loading />;
  }

  const hasDisplayableIntegrationAttributes = hasDisplayableAttributes(
    site.integrationAttributes ?? []
  );

  // Filter attributes for PropFit tab
  const propfitAttributes = site.attributes?.filter((attrRef: any) => {
    const attrData = getFragmentData(AttributeFieldsFragment, attrRef);
    return attrData?.id && !attrData.id.startsWith('integration_');
  });

  const address = site.address && site.address !== '{}' && (
    <AddressLink data={site.address} longFormat />
  );

  return (
    <SideLayout isPopup={isPopup} id='site-view-container'>
      {!isPopup && (
        <div className='block'>
          <SideLayout.Head
            onClose={onClose}
            rightSlot={
              <Link
                className='ml-1 mt-1.5 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full hover:bg-grey-10'
                to='edit'
              >
                <FontAwesomeIcon
                  icon={faPen as IconProp}
                  className='h-4 text-secondary'
                />
              </Link>
            }
          >
            {site.name}
          </SideLayout.Head>
        </div>
      )}
      <SideLayout.Body className='flex flex-col gap-4 px-4 py-2'>
        <div className='flex gap-5 rounded-md border border-grey-20 p-3'>
          <div className={classNames('h-32 w-32 overflow-hidden rounded ')}>
            {site.image ? (
              <Gallery size='fit' images={site.image} />
            ) : (
              <BuildingPlaceholder className='text-5xl' />
            )}
          </div>

          <div className='flex flex-col gap-2'>
            <p className='text-tertiary'>{site.type}</p>
            {address && address}

            {site.licensor?.name && (
              <div className='flex flex-col gap-0'>
                <p className='text-sm text-tertiary'>Owner</p>
                <p className=''>{site.licensor?.name}</p>
              </div>
            )}
          </div>
        </div>

        <div className='w-auto flex-1 rounded-t-2xl border border-grey-20 bg-grey-2'>
          <TabGroup
            selectedIndex={selectedTabIndex}
            onChange={setSelectedTabIndex}
          >
            <div className='bg-blue-900/20 relative overflow-hidden rounded-t-2xl bg-white'>
              <TabList className='relative z-10 flex'>
                <TabHeader title='Attributes' />
                <TabHeader title='Activity' />
                <TabHeader title='Jobs' />
                {/* <TabHeader title='Items' /> */}
                <TabHeader title='Spaces' />
                <TabHeader title='Assets' />
              </TabList>

              {/* Sliding active indicator */}
              <motion.div
                className='absolute bottom-0 left-0 border border-brand bg-brand shadow'
                style={{
                  width: `${100 / 5}%`, // 5 tabs
                  height: '100%',
                }}
                animate={{
                  x: `${selectedTabIndex * 100}%`,
                }}
                transition={{
                  duration: 0.2,
                  ease: 'easeInOut',
                }}
              />
            </div>
            <TabPanels className=''>
              <TabPanel className='relative flex min-h-[20rem] flex-col'>
                <AttributesList
                  entityId={`Site:${site.id}`}
                  entityTypeName={t('site')}
                  attributes={propfitAttributes}
                  // @ts-expect-error FIXME
                  integrationAttributes={site.integrationAttributes}
                  integrationName={data?.integrations?.[0]}
                  onUpdate={() => reload()}
                  isSearchSticked={isSearchSticked}
                  showIntegrationView={hasDisplayableIntegrationAttributes}
                  siteId={siteId}
                  hideEdit={isPopup}
                />
              </TabPanel>
              <TabPanel className='relative flex min-h-[20rem] flex-col'>
                {loadedTabs.has(1) ? (
                  <Suspense fallback={<Loading />}>
                    <ActivityTab siteId={siteId} />
                  </Suspense>
                ) : null}
              </TabPanel>
              <TabPanel
                className='relative flex min-h-[20rem] flex-col'
                id='jobs-tab-panel'
              >
                {loadedTabs.has(2) ? (
                  <Suspense fallback={<Loading />}>
                    <JobsList siteId={siteId} />
                  </Suspense>
                ) : null}
              </TabPanel>
              {/* <TabPanel className='relative flex min-h-[20rem] flex-col bg-grey-2'>
                <StockTab siteId={siteId} />
              </TabPanel> */}{' '}
              <TabPanel
                className='relative flex min-h-[20rem] flex-col'
                id='spaces-tab-panel'
              >
                {loadedTabs.has(3) ? (
                  <Suspense fallback={<Loading />}>
                    <SpacesList siteId={siteId} />
                  </Suspense>
                ) : null}
              </TabPanel>
              <TabPanel
                className='relative flex min-h-[20rem] flex-col'
                id='assets-tab-panel'
              >
                {loadedTabs.has(4) ? (
                  <Suspense fallback={<Loading />}>
                    <AssetsList siteId={siteId} site={site} />
                  </Suspense>
                ) : null}
              </TabPanel>
            </TabPanels>
          </TabGroup>
        </div>
      </SideLayout.Body>
    </SideLayout>
  );
}
